import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  TemplateRef,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Subject, debounceTime, takeUntil } from 'rxjs';

interface Breakpoint {
  minWidth: number;
  itemsPerPage: number;
}

interface TouchPoint {
  clientX: number;
  clientY: number;
}

@Component({
  selector: 'umkt-carousel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CarouselComponent implements OnInit, OnDestroy {
  @Input() showArrows: boolean = true;

  @Input()
  set items(value: any[]) {
    this._items.set(value || []);
  }
  get items(): any[] {
    return this._items();
  }

  @Input()
  set breakpoints(value: Breakpoint[]) {
    this._breakpoints.set(value || this.defaultBreakpoints);
  }
  get breakpoints(): Breakpoint[] {
    return this._breakpoints();
  }

  @Input() enableSwipe: boolean = true;
  @Input() swipeThreshold: number = 50;
  @Input() transitionDuration: number = 500;

  @Output() pageChange = new EventEmitter<number>();

  @ContentChild('itemTemplate') itemTemplate!: TemplateRef<any>;

  // Breakpoints padrão
  private readonly defaultBreakpoints: Breakpoint[] = [
    { minWidth: 0, itemsPerPage: 1 }, // Mobile portrait (350px card)
    { minWidth: 480, itemsPerPage: 1 }, // Mobile landscape (350px card)
    { minWidth: 768, itemsPerPage: 2 }, // Tablet portrait (350px * 2 + 16px gap)
    { minWidth: 1024, itemsPerPage: 2 }, // Tablet landscape (350px * 2 + 16px gap)
    { minWidth: 1440, itemsPerPage: 3 }, // Desktop (350px * 3 + 32px gaps)
    { minWidth: 1564, itemsPerPage: 4 }, // Desktop (350px * 4 + 48px gaps)
    { minWidth: 1920, itemsPerPage: 4 }, // Large Desktop (350px * 4 + 24px gaps)
    { minWidth: 2560, itemsPerPage: 6 }, // Extra Large Desktop (350px * 6 + 80px gaps)
  ];

  // Signals
  private readonly _items = signal<any[]>([]);
  private readonly _breakpoints = signal<Breakpoint[]>([]);
  private readonly _windowWidth = signal(0);
  private readonly _isTransitioning = signal(false);
  private readonly _currentIndex = signal(0);

  /** App chrome breakpoint: arrows only at this width and above. */
  static readonly DESKTOP_MIN_WIDTH = 1024;

  // Computed values
  readonly currentIndex = this._currentIndex.asReadonly();
  readonly isTransitioning = this._isTransitioning.asReadonly();

  /** Web only — mobile/tablet rely on swipe and must not render arrow controls. */
  readonly isDesktopViewport = computed(
    () => this._windowWidth() >= CarouselComponent.DESKTOP_MIN_WIDTH
  );

  readonly visibleItems = computed(() => {
    const breakpoints = this._breakpoints();
    const windowWidth = this._windowWidth();

    if (!this.isBrowser || windowWidth === 0) {
      return breakpoints[0]?.itemsPerPage || 1;
    }

    const sortedBreakpoints = [...breakpoints].sort(
      (a, b) => b.minWidth - a.minWidth
    );

    const currentBreakpoint =
      sortedBreakpoints.find(bp => windowWidth >= bp.minWidth) ||
      breakpoints[0];
    return currentBreakpoint.itemsPerPage;
  });

  readonly totalPages = computed(() => {
    const items = this._items();
    const visibleItems = this.visibleItems();

    if (items.length <= visibleItems) return 1;
    return Math.ceil(items.length / visibleItems);
  });

  readonly currentPage = computed(() => {
    const currentIndex = this._currentIndex();
    const visibleItems = this.visibleItems();
    return Math.floor(currentIndex / visibleItems);
  });

  readonly isFirstPage = computed(() => this._currentIndex() === 0);
  readonly isLastPage = computed(() => {
    const currentIndex = this._currentIndex();
    const items = this._items();
    const visibleItems = this.visibleItems();

    return (
      items.length <= visibleItems ||
      currentIndex >= items.length - visibleItems
    );
  });
  readonly hasItems = computed(() => this._items().length > 0);

  private touchStart: TouchPoint | null = null;
  private touchEnd: TouchPoint | null = null;
  private isDragging: boolean = false;
  private dragOffset: number = 0;

  private resizeSubject = new Subject<void>();
  private destroy$ = new Subject<void>();
  private transitionTimeout: any = null;

  isBrowser: boolean;

  @HostListener('window:resize')
  onResize() {
    if (this.isBrowser) {
      this.resizeSubject.next();
    }
  }
  private cdr = inject(ChangeDetectorRef);
  private platformId = inject(PLATFORM_ID);
  private elementRef = inject(ElementRef);

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.resizeSubject
      .pipe(debounceTime(150), takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateWindowWidth();
      });

    effect(() => {
      const currentPage = this.currentPage();
      this.pageChange.emit(currentPage);
    });
  }

  ngOnInit() {
    if (this._items().length === 0) {
      this._items.set(this.items);
    }
    if (this._breakpoints().length === 0) {
      this._breakpoints.set(this.defaultBreakpoints);
    }
    this.updateWindowWidth();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearTransitionTimeout();
  }

  private clearTransitionTimeout() {
    if (this.transitionTimeout) {
      clearTimeout(this.transitionTimeout);
      this.transitionTimeout = null;
    }
  }

  // Métodos de navegação
  nextPage() {
    const currentIndex = this._currentIndex();
    const items = this._items();
    const visibleItems = this.visibleItems();

    if (
      items.length <= visibleItems ||
      currentIndex >= items.length - visibleItems ||
      this._isTransitioning()
    )
      return;

    this.clearTransitionTimeout();
    this._isTransitioning.set(true);
    
    // Avança baseado no número de itens visíveis por página
    const nextIndex = Math.min(currentIndex + visibleItems, items.length - visibleItems);
    this._currentIndex.set(nextIndex);

    this.transitionTimeout = setTimeout(() => {
      this._isTransitioning.set(false);
    }, this.transitionDuration);
  }

  previousPage() {
    const currentIndex = this._currentIndex();
    const visibleItems = this.visibleItems();

    if (currentIndex <= 0 || this._isTransitioning()) return;

    this.clearTransitionTimeout();
    this._isTransitioning.set(true);
    
    // Retrocede baseado no número de itens visíveis por página
    const prevIndex = Math.max(currentIndex - visibleItems, 0);
    this._currentIndex.set(prevIndex);

    this.transitionTimeout = setTimeout(() => {
      this._isTransitioning.set(false);
    }, this.transitionDuration);
  }

  goToPage(page: number) {
    const totalPages = this.totalPages();
    const visibleItems = this.visibleItems();

    if (page < 0 || page >= totalPages || this._isTransitioning()) return;

    const newIndex = page * visibleItems;

    this.clearTransitionTimeout();
    this._isTransitioning.set(true);
    this._currentIndex.set(newIndex);

    this.transitionTimeout = setTimeout(() => {
      this._isTransitioning.set(false);
    }, this.transitionDuration);
  }

  // Método para resetar a posição do carrossel
  resetPosition() {
    if (this._currentIndex() === 0) return;

    this.clearTransitionTimeout();
    this._isTransitioning.set(true);
    this._currentIndex.set(0);

    this.transitionTimeout = setTimeout(() => {
      this._isTransitioning.set(false);
    }, this.transitionDuration);
  }

  // Método para navegar para um produto específico
  scrollToProduct(productUuid: string, retryCount: number = 0) {
    const items = this._items();
    const productIndex = items.findIndex(item => item.uuid === productUuid);
    
    if (productIndex === -1) {
      // If product not found and we haven't retried too many times, try again
      if (retryCount < 10) {
        setTimeout(() => {
          this.scrollToProduct(productUuid, retryCount + 1);
        }, 100);
      }
      return;
    }
    
    const visibleItems = this.visibleItems();
    const targetPage = Math.floor(productIndex / visibleItems);
    
    this.goToPage(targetPage);
  }

  // Métodos de touch/drag
  onTouchStart(event: globalThis.TouchEvent) {
    if (!this.enableSwipe || this._isTransitioning()) return;
    
     if (this.isInteractiveTarget(event.target)) return; 

    this.touchStart = {
      clientX: event.touches[0].clientX,
      clientY: event.touches[0].clientY,
    };
    this.isDragging = true;
    this.dragOffset = 0;
  }

  onTouchMove(event: globalThis.TouchEvent) {
    if (!this.isDragging || !this.touchStart) return;

    const currentTouch = {
      clientX: event.touches[0].clientX,
      clientY: event.touches[0].clientY,
    };

    const deltaX = currentTouch.clientX - this.touchStart.clientX;
    const deltaY = currentTouch.clientY - this.touchStart.clientY;

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      event.preventDefault();
      this.dragOffset = deltaX;
      this.updateDragTransform();
    }
  }

  onTouchEnd(event: globalThis.TouchEvent) {
    if (!this.isDragging || !this.touchStart) return;

      if (this.isInteractiveTarget(event.target)) { 
        this.resetTouchState();
      return;
    }

    this.touchEnd = {
      clientX: event.changedTouches[0].clientX,
      clientY: event.changedTouches[0].clientY,
    };

    const deltaX = this.touchEnd.clientX - this.touchStart.clientX;
    const deltaY = this.touchEnd.clientY - this.touchStart.clientY;

    if (
      Math.abs(deltaX) > Math.abs(deltaY) &&
      Math.abs(deltaX) > this.swipeThreshold
    ) {
      this.handleDragNavigation(deltaX);
    }

    this.resetTouchState();
  }

  private isInteractiveTarget(target: EventTarget | null): boolean {
    if (!target || !(target as HTMLElement).closest) return false;

    return !!(target as HTMLElement).closest(
      'select, option, button, a'
    );
  }

  onMouseDown(event: MouseEvent) {
    if (!this.enableSwipe || this._isTransitioning()) return;
    if (this.isInteractiveTarget(event.target)) return; 

    this.touchStart = {
      clientX: event.clientX,
      clientY: event.clientY,
    };
    this.isDragging = true;
    this.dragOffset = 0;
  }

  onMouseMove(event: MouseEvent) {
    if (!this.isDragging || !this.touchStart) return;

    const deltaX = event.clientX - this.touchStart.clientX;
    const deltaY = event.clientY - this.touchStart.clientY;

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      event.preventDefault();
      this.dragOffset = deltaX;
      this.updateDragTransform();
    }
  }

  onMouseUp(event: MouseEvent) {
    if (!this.isDragging || !this.touchStart) return;

     if (this.isInteractiveTarget(event.target)) { 
      this.resetTouchState(); 
      return;
    }

    this.touchEnd = {
      clientX: event.clientX,
      clientY: event.clientY,
    };

    const deltaX = this.touchEnd.clientX - this.touchStart.clientX;
    const deltaY = this.touchEnd.clientY - this.touchStart.clientY;

    if (
      Math.abs(deltaX) > Math.abs(deltaY) &&
      Math.abs(deltaX) > this.swipeThreshold
    ) {
      this.handleDragNavigation(deltaX);
    }

    this.resetTouchState();
  }
   onMouseLeave() {
    if (!this.isDragging) return;
    this.resetTouchState(); 
  }

  private updateDragTransform() {
    const container =
      this.elementRef.nativeElement.querySelector('.carousel__items');
    if (container) {
      const itemWidth = this.getCardWidth();
      const gap = this.getCardGap();
      const currentOffset = this._currentIndex() * (itemWidth + gap);
      const translateX = -currentOffset + this.dragOffset;
      container.style.transform = `translateX(${translateX}px)`;
      container.style.transition = 'none';
    }
  }

  private resetTouchState() {
    this.isDragging = false;
    this.touchStart = null;
    this.touchEnd = null;
    this.dragOffset = 0;

    const container =
      this.elementRef.nativeElement.querySelector('.carousel__items');
    if (container) {
      const itemWidth = this.getCardWidth();
      const gap = this.getCardGap();
      const currentOffset = this._currentIndex() * (itemWidth + gap);
      container.style.transform = `translateX(${-currentOffset}px)`;
      container.style.transition = `transform ${this.transitionDuration}ms ease-in-out`;
    }
  }

  private handleDragNavigation(deltaX: number) {
    const itemWidth = this.getCardWidth();
    const gap = this.getCardGap();
    const totalItemWidth = itemWidth + gap;

    const draggedItems = Math.floor(Math.abs(deltaX) / totalItemWidth);
    
    if (draggedItems === 0) {
      if (deltaX > 0) {
        this.previousPage();
      } else {
        this.nextPage();
      }
    } else {
      const currentIndex = this._currentIndex();
      const items = this._items();
      const visibleItems = this.visibleItems();
      
      if (deltaX > 0) {
        const newIndex = Math.max(currentIndex - (draggedItems * visibleItems), 0);
        this.goToIndex(newIndex);
      } else {
        const maxIndex = Math.max(items.length - visibleItems, 0);
        const newIndex = Math.min(currentIndex + (draggedItems * visibleItems), maxIndex);
        this.goToIndex(newIndex);
      }
    }
  }

  private goToIndex(index: number) {
    if (this._isTransitioning()) return;
    
    this.clearTransitionTimeout();
    this._isTransitioning.set(true);
    this._currentIndex.set(index);

    this.transitionTimeout = setTimeout(() => {
      this._isTransitioning.set(false);
    }, this.transitionDuration);
  }

  private updateWindowWidth() {
    if (!this.isBrowser) {
      this._windowWidth.set(0);
      return;
    }

    this._windowWidth.set(window.innerWidth);
    this.cdr.markForCheck();
  }

  get currentBreakpointInfo(): string {
    const visibleItems = this.visibleItems();
    return `${visibleItems} item${visibleItems > 1 ? 's' : ''} por página`;
  }

  getCardWidth(): number {
    if (!this.isBrowser) return 350;

    const windowWidth = this._windowWidth();

    if (windowWidth <= 479) return 280;
    if (windowWidth <= 767) return 300;
    return 350;
  }

  getCardGap(): number {
    if (!this.isBrowser) return 16;

    const windowWidth = this._windowWidth();

    if (windowWidth <= 479) return 12;
    if (windowWidth <= 767) return 14;
    return 16;
  }
}
